import { useEffect, useMemo, useRef, useState } from "react";
import { Folder, Minimize2, Send, Sparkles } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AgentRole } from "@paperclip-cloud/shared";
import { useAgentChat } from "../hooks/useAgentChat";
import { useFleet } from "../hooks/useFleet";
import { agentHostsApi, agentMessagesApi } from "../api/queries/agents";
import { workspacesApi } from "../api/queries/workspaces";
import { toast } from "./Toast";
import { ToolCallCard } from "./ToolCallCard";
import { StatusDot } from "./StatusDot";
import { Modal } from "./Modal";
import { WorkspacesPanel, workspacesQueryKey } from "./WorkspacesPanel";
import { useUiStore } from "../stores/ui";

export interface AgentChatPanelProps {
  companyId: string | null;
  role: AgentRole;
}

const ROLE_TITLE: Record<AgentRole, string> = {
  ceo: "CEO",
  ops: "Operations",
};

const ROLE_PLACEHOLDER: Record<AgentRole, string> = {
  ceo: "Message the CEO…",
  ops: "Message Operations…",
};

const ROLE_EMPTY_HINT: Record<AgentRole, { headline: string; example: string }> = {
  ceo: {
    headline: "Ask the CEO to plan, dispatch, or summarize work.",
    example: "transcribe intro.mp4 — use the GPU box if it’s online.",
  },
  ops: {
    headline: "Ask Operations to triage, prioritize, or report on the queue.",
    example: "what’s blocked, and which devices have been idle the longest?",
  },
};

export function AgentChatPanel({ companyId, role }: AgentChatPanelProps) {
  const { messages, draft, setDraft, send, status } = useAgentChat(companyId, role);
  const fleet = useFleet(companyId);
  const hosts = useQuery({
    queryKey: ["agent-hosts", companyId],
    queryFn: () => agentHostsApi.list(companyId!),
    enabled: !!companyId,
    staleTime: 10_000,
  });

  const host = hosts.data?.agent_hosts.find((h) => h.role === role);
  const hostDevice = host?.device_id
    ? fleet.data?.devices.find((d) => d.id === host.device_id)
    : null;
  const hostOnline = !!host?.enabled && !!hostDevice?.online;

  const [pending, setPending] = useState(false);
  const [showCompressed, setShowCompressed] = useState(false);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const qc = useQueryClient();

  // Folder-picker state. Only meaningful when this role has a host device.
  const hostDeviceId = host?.device_id ?? null;
  const selectedWorkspacePath = useUiStore((s) =>
    hostDeviceId ? s.selectedWorkspaceByDevice[hostDeviceId] : undefined,
  );
  const setSelectedWorkspace = useUiStore((s) => s.setSelectedWorkspace);

  // Prefetch the host's workspaces lazily — only enabled when we have a host
  // device — so we can render the chip's display name without opening the
  // modal first.
  const wsQuery = useQuery({
    queryKey: hostDeviceId
      ? workspacesQueryKey(hostDeviceId)
      : ["device-workspaces", "none"],
    queryFn: () => workspacesApi.get(hostDeviceId!),
    enabled: !!hostDeviceId,
    staleTime: 30_000,
  });

  const selectedWorkspace = useMemo(() => {
    if (!selectedWorkspacePath) return null;
    const report = wsQuery.data?.workspaces;
    return report?.workspaces.find((w) => w.path === selectedWorkspacePath) ?? null;
  }, [selectedWorkspacePath, wsQuery.data]);

  // Hide messages folded into a compression summary unless the user opts in.
  // The summary itself (a system-author row) survives because its own
  // `compressed_into_id` is null.
  const compressedCount = messages.filter((m) => m.compressed_into_id).length;
  const visibleMessages = showCompressed
    ? messages
    : messages.filter((m) => !m.compressed_into_id);

  const compress = useMutation({
    mutationFn: () => agentMessagesApi.compress(companyId!, role),
    onSuccess: (res) => {
      toast(`Compressed ${res.rows_compressed} messages`, "success");
      qc.invalidateQueries({ queryKey: ["agent-messages", companyId, role] });
      setShowCompressed(false);
    },
    onError: (err: Error) =>
      toast(err.message ?? "Compress failed", "error"),
  });

  // Disabled while host is offline / nothing to compress / compress mutation in flight.
  const canCompress =
    !!companyId &&
    hostOnline &&
    visibleMessages.filter((m) => !m.streaming).length >= 4 &&
    !compress.isPending;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, showCompressed]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.trim() || !companyId) return;
    setPending(true);
    try {
      // Attach the selected folder (if any) so the hub can thread it through
      // to the dispatch / system prompt for this turn.
      await send(draft, selectedWorkspacePath ?? null);
    } finally {
      setPending(false);
      requestAnimationFrame(() => taRef.current?.focus());
    }
  };

  const title = ROLE_TITLE[role];
  const empty = ROLE_EMPTY_HINT[role];

  return (
    <section
      className="flex h-full min-h-0 flex-col bg-surface"
      aria-label={`${title} chat`}
    >
      <header className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-accent" aria-hidden="true" />
          <h2 className="text-[13px] font-semibold tracking-[-0.005em] text-primary">
            {title}
          </h2>
          {host?.model && <span className="badge badge--accent">{host.model}</span>}
          <StatusDot
            tone={hostOnline ? "running" : "idle"}
            title={hostOnline ? `${title} host online` : `${title} host offline`}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() =>
              window.confirm(
                `Compress this ${title} conversation into a short summary?\n\nOlder messages will be hidden but kept in the audit log. New messages start a fresh context.`,
              ) && compress.mutate()
            }
            disabled={!canCompress}
            title={
              !hostOnline
                ? `${title} host is offline`
                : compressedCount > 0
                  ? "Compress everything since the last summary"
                  : "Compress this conversation"
            }
          >
            <Minimize2 size={12} aria-hidden="true" />
            {compress.isPending ? "Compressing…" : "Compress"}
          </button>
          <span className="micro">
            {status === "streaming"
              ? "streaming…"
              : `${visibleMessages.length} msgs${
                  compressedCount > 0 && !showCompressed
                    ? ` · ${compressedCount} compressed`
                    : ""
                }`}
          </span>
        </div>
      </header>
      {compressedCount > 0 && (
        <div className="border-b border-border-subtle bg-raised px-4 py-1.5 text-[11px] text-tertiary">
          <button
            type="button"
            className="hover:underline"
            onClick={() => setShowCompressed((x) => !x)}
          >
            {showCompressed
              ? `Hide ${compressedCount} compressed message${compressedCount === 1 ? "" : "s"}`
              : `Show ${compressedCount} compressed message${compressedCount === 1 ? "" : "s"}`}
          </button>
        </div>
      )}

      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-auto px-4 py-3"
        aria-live="polite"
        aria-label={`${title} chat history`}
      >
        {visibleMessages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-secondary">
            <Sparkles size={24} className="text-tertiary" aria-hidden="true" />
            <p className="text-[13px]">{empty.headline}</p>
            <p className="text-[12px] text-tertiary">
              e.g. <em>“{empty.example}”</em>
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {visibleMessages.map((m) => (
              <li key={m.id} className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span
                    className={`micro ${m.author === "user" ? "text-secondary" : "text-accent"}`}
                  >
                    {m.author === "user"
                      ? "You"
                      : m.author === "agent"
                        ? title
                        : m.author === "tool"
                          ? "Tool"
                          : "System"}
                  </span>
                </div>
                {m.toolCalls.map((c) => (
                  <ToolCallCard key={c.id} call={c} />
                ))}
                {m.content && (
                  <div className="text-[13px] leading-[20px] text-primary whitespace-pre-wrap break-words">
                    {m.content}
                    {m.streaming && <span className="caret-blink">▍</span>}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <form onSubmit={onSubmit} className="border-t border-border-subtle p-3">
        {hostDeviceId && (
          <div className="mb-2 flex items-center gap-2">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setFolderModalOpen(true)}
              title={
                selectedWorkspace?.path ??
                selectedWorkspacePath ??
                "No folder selected — uses the device's default cwd"
              }
              aria-label="Choose working folder"
            >
              <Folder size={12} aria-hidden="true" />
              {selectedWorkspace
                ? selectedWorkspace.name
                : selectedWorkspacePath
                  ? selectedWorkspacePath
                  : "No folder (default cwd)"}
            </button>
            {selectedWorkspacePath && (
              <button
                type="button"
                className="micro hover:text-primary"
                onClick={() => setSelectedWorkspace(hostDeviceId, null)}
                aria-label="Clear working folder"
              >
                clear
              </button>
            )}
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={taRef}
            className="input flex-1 font-text"
            placeholder={companyId ? ROLE_PLACEHOLDER[role] : "Select a company first"}
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={!companyId || pending}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSubmit(e);
              }
            }}
            aria-label={`Message ${title}`}
          />
          <button
            className="btn btn--primary"
            type="submit"
            disabled={!companyId || !draft.trim() || pending}
            aria-label="Send message"
          >
            <Send size={14} />
            Send
          </button>
        </div>
        <div className="mt-1 text-[11px] text-tertiary">
          Enter to send · Shift+Enter for newline
        </div>
      </form>

      {hostDeviceId && (
        <Modal
          open={folderModalOpen}
          onClose={() => setFolderModalOpen(false)}
          title="Working folder"
          size="md"
        >
          <WorkspacesPanel
            deviceId={hostDeviceId}
            hideHeader
            onPick={() => setFolderModalOpen(false)}
          />
        </Modal>
      )}
    </section>
  );
}
